
import React, { useEffect, useRef, useState } from 'react';
import { TestTube, FileText, Video, FileCheck } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import CarouselEntry from './CarouselEntry';

const services = [
  {
    id: 'glycemic-tests',
    title: 'Glycemic Impact Tests',
    contentHtml: '• <span class="font-semibold">Glycemic impact data</span> of your product (glycemic scores, glucose response curves)<br/>• <span class="font-semibold">Anonymized panelist feedback</span> – product survey and satiety impact<br/>• <span class="font-semibold">Demographic information</span> and key metabolic health indicators<br/>• <span class="font-semibold">Side-by-side comparisons</span> for:<br/><br/><span class="ml-4 block">Glucose spike lowering products:</span><span class="ml-4 block text-neutral-600">Compare carb-induced meal with vs. without the product</span><br/><span class="ml-4 block">Low glycemic foods & drinks:</span><span class="ml-4 block text-neutral-600">Compare product glucose response vs. baseline sugar sensitivity</span><br/><br/><span class="text-sm text-neutral-500 italic">On average, we need 8 weeks and 300 product samples to collect 10 qualifying participant responses. Using incentives and marketing boosts, we can further speed up the collection process.</span>',
    icon: TestTube,
    imageUrl: '/images/join-page/glycemic-impact-tests.png',
    color: '#8B5CF6',
    isInstagramPreview: false,
  },
  {
    id: 'verified-testimonials',
    title: 'Verified User Testimonials',
    contentHtml: '• <span class="font-semibold">Vertical (9:16) format</span> optimized for Instagram Reels, TikTok, and YouTube Shorts<br/>• <span class="font-semibold">30-60 seconds in length</span>, shot in HD (1080p)<br/>• <span class="font-semibold">Professionally edited</span> with branded overlays if desired<br/>• <span class="font-semibold">Real tester sharing</span> product experience + on-screen glucose response curve<br/><br/><span class="font-semibold">Includes full commercial rights</span> for social media, website, ads, and PR<br/><br/><span class="text-sm text-neutral-500 italic">Note: Panelists are compensated for testing protocol, not product promotion. All feedback is authentic and unfiltered.</span><br/><br/><a href="https://www.instagram.com/reel/Cqd6oS4OP87/" target="_blank" rel="noopener noreferrer" class="text-primary text-sm font-medium hover:underline inline-flex items-center">See example <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 ml-1"><path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clip-rule="evenodd" /></svg></a>',
    icon: FileText,
    imageUrl: '/images/join-page/verified-user-testimonials.png',
    color: '#0EA5E9',
    isInstagramPreview: true,
    instagramData: {
      thumbnailUrl: "/images/join-page/verified-user-testimonials.png",
      instagramUrl: "https://www.instagram.com/reel/Cqd6oS4OP87/"
    }
  },
  {
    id: 'influencer-content',
    title: 'Validated Influencer Video Posts',
    contentHtml: '• <span class="font-semibold">60-90 second vertical video</span> (TikTok/Reels/YouTube format)<br/>• <span class="font-semibold">Created by vetted health influencer</span> who tested your product with CGM<br/>• <span class="font-semibold">Authentic commentary</span> showing product use, glucose response data, and honest opinion<br/>• <span class="font-semibold">Not a paid endorsement</span> - just real data-backed experience<br/><br/><span class="font-semibold">Includes full commercial rights</span> for social media, website, ads, and email marketing<br/><br/><a href="https://www.instagram.com/reel/C4t4DTbJRNS" target="_blank" rel="noopener noreferrer" class="text-primary text-sm font-medium hover:underline inline-flex items-center">See example <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 ml-1"><path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clip-rule="evenodd" /></svg></a>',
    icon: Video,
    imageUrl: '/images/join-page/validated-influencer-post.png',
    color: '#F97316',
    isInstagramPreview: true,
    instagramData: {
      thumbnailUrl: "/images/join-page/validated-influencer-post.png",
      instagramUrl: "https://www.instagram.com/reel/C4t4DTbJRNS"
    }
  },
  {
    id: 'consumer-evidence-report',
    title: 'Consumer Evidence Report',
    contentHtml: '• <span class="font-semibold">Real-world biometric data</span> collected from users wearing CGMs, logging satiety, energy, and cravings<br/>• <span class="font-semibold">Third-party expert validation</span> with scientific panel review to ensure data integrity<br/>• <span class="font-semibold">Polished, shareable report</span> for retail buyers, investors, and scientific storytelling<br/>• <span class="font-semibold">Data-backed credibility signal</span> measured and validated by a neutral party<br/><br/><span class="font-semibold">Your stamp of approval</span> when speaking to retail buyers, investors, or co-authoring white papers<br/><br/><a href="https://tastermonial.com/pages/brandservices" target="_blank" rel="noopener noreferrer" class="text-primary text-sm font-medium hover:underline inline-flex items-center">Read more <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 ml-1"><path fill-rule="evenodd" d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5a.75.75 0 00-.75-.75h-7.5a.75.75 0 000 1.5h5.69l-7.22 7.22a.75.75 0 000 1.06z" clip-rule="evenodd" /></svg></a><br/><br/><span class="text-sm text-neutral-500 italic">Note: Not a clinical trial or FDA-submittable. Not for disease or therapeutic claims. Must include appropriate disclaimers.</span>',
    icon: FileCheck,
    imageUrl: '/images/join-page/consumer-evidence-report.png',
    color: '#10B981',
    isInstagramPreview: false,
  },
];

const SocialProofSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.animate-on-scroll');
            elements.forEach((el, i) => {
              setTimeout(() => {
                el.classList.add('visible');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  // Update activeIndex when the carousel changes
  useEffect(() => {
    if (!carouselApi) return;

    const handleSelect = () => {
      setActiveIndex(carouselApi.selectedScrollSnap());
    };

    carouselApi.on("select", handleSelect);
    return () => {
      carouselApi.off("select", handleSelect);
    };
  }, [carouselApi]);

  // Listen for the custom event to select a specific carousel item
  useEffect(() => {
    const handleCustomEvent = (event: CustomEvent) => {
      const { index } = event.detail;
      if (carouselApi && typeof index === 'number') {
        carouselApi.scrollTo(index);
        setActiveIndex(index);

        // Scroll the section into view if not already visible
        sectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    // Add event listener
    document.addEventListener('select-carousel-item', handleCustomEvent as EventListener);

    // Clean up
    return () => {
      document.removeEventListener('select-carousel-item', handleCustomEvent as EventListener);
    };
  }, [carouselApi]);

  // Function to handle manual navigation through dots
  const handleDotClick = (index: number) => {
    if (carouselApi) {
      carouselApi.scrollTo(index);
    }
    setActiveIndex(index);
  };

  return (
    <section ref={sectionRef} id="tastermonial-services" className="section-padding bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-12 animate-on-scroll">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            The Tastermonial Services
          </h2>
          <p className="text-neutral-700 text-lg">
            Transforming data into trusted marketing assets for your brand
          </p>
        </div>

        <div className="animate-on-scroll">
          <Carousel
            opts={{
              loop: true,
              align: "start",
            }}
            className="w-full"
            setApi={setCarouselApi}
          >
            <CarouselContent>
              {services.map((service) => (
                <CarouselItem key={service.id}>
                  <div className="p-1">
                    <Card className="border-none shadow-sm">
                      <CardContent className="p-0">
                        <CarouselEntry
                          title={service.title}
                          icon={service.icon}
                          imageUrl={service.imageUrl}
                          color={service.color}
                          contentHtml={service.contentHtml}
                          isInstagramPreview={service.isInstagramPreview}
                          instagramData={service.instagramData}
                        />
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center justify-center gap-2 mt-8">
              <CarouselPrevious className="static" />
              <CarouselNext className="static" />
            </div>
          </Carousel>

          {/* Carousel indicator dots */}
          <div className="flex justify-center mt-6 gap-2">
            {services.map((_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeIndex ? 'bg-primary w-6' : 'bg-neutral-300'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialProofSection;
